import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { IMAGES, PROJECTS } from "../data/site";
import { SectionEyebrow } from "../components/SectionEyebrow";

export default function Impacto() {
    return (
        <>
            <section className="topo-bg py-20 lg:py-28" data-testid="impact-hero">
                <div className="container-fe max-w-4xl animate-fade-up">
                    <SectionEyebrow>Impacto y proyectos</SectionEyebrow>
                    <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05]">
                        Modelos que <span className="text-brand-green">transforman</span>, no que asisten.
                    </h1>
                    <p className="mt-7 text-lg text-muted-foreground leading-relaxed max-w-2xl">
                        Nuestros programas están diseñados para ampliar la visión, fortalecer
                        capacidades y abrir oportunidades reales. Cada proyecto nace de una escucha
                        atenta del territorio.
                    </p>
                </div>
            </section>

            {/* Model explanation */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <SectionEyebrow>Nuestro modelo</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Desarrollo de talentos a través de experiencias transformadoras.
                        </h2>
                        <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed text-lg">
                            <p>
                                Trabajamos con un enfoque de <strong className="text-brand-navy">desarrollo humano integral</strong>:
                                identificamos las habilidades únicas de cada persona y las acompañamos con
                                experiencias que amplían su visión del mundo.
                            </p>
                            <p>
                                Refuerzo escolar, acompañamiento psicosocial, interacción cultural,
                                jornadas recreativas, escuela de padres y jornadas de integración con
                                adulto mayor. Estas no son actividades aisladas: son herramientas
                                poderosas para fortalecer la identidad, construir comunidad y
                                descubrir el propio potencial.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="aspect-square rounded-2xl overflow-hidden ring-1 ring-black/5">
                            <img src={IMAGES.child_gift} alt="Niño con juego" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-square rounded-2xl overflow-hidden ring-1 ring-black/5 mt-8">
                            <img src={IMAGES.volunteer_painting} alt="Pintura facial" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-square rounded-2xl overflow-hidden ring-1 ring-black/5 mt-[-20px]">
                            <img src={IMAGES.elderly_care} alt="Adulta mayor" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-square rounded-2xl overflow-hidden ring-1 ring-black/5">
                            <img src={IMAGES.children_community} alt="Comunidad" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Projects grid */}
            <section className="py-20 lg:py-28 bg-brand-cream" data-testid="projects-section">
                <div className="container-fe">
                    <div className="max-w-2xl">
                        <SectionEyebrow>Programas activos</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Seis programas, una sola convicción.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {PROJECTS.map((p) => (
                            <article
                                key={p.slug}
                                data-testid={`project-card-${p.slug}`}
                                className="group rounded-2xl overflow-hidden bg-white border border-black/5 hover:border-brand-green/30 hover:-translate-y-1 transition-all"
                            >
                                <div className="aspect-[4/3] overflow-hidden bg-muted">
                                    <img
                                        src={IMAGES[p.image]}
                                        alt={p.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs uppercase tracking-wider font-bold text-brand-green">{p.category}</div>
                                    <h3 className="mt-3 font-display text-xl font-semibold text-brand-navy leading-snug">{p.title}</h3>
                                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.summary}</p>
                                    <div className="mt-5 pt-5 border-t border-black/5 flex items-center justify-between">
                                        <div className="flex items-center gap-1.5 text-sm font-semibold text-brand-navy">
                                            <Sparkles size={14} className="text-brand-green" />
                                            {p.impact}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Storytelling */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe">
                    <div className="rounded-3xl bg-brand-navy p-10 lg:p-16 relative overflow-hidden">
                        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${IMAGES.hero_topo})`, backgroundSize: "cover" }} />
                        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <SectionEyebrow className="[&>span:last-child]:text-brand-green">Una historia real</SectionEyebrow>
                                <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-white leading-tight">
                                    "Pensé que mi futuro era el barrio. Hoy lo que enseño es el barrio que soñé."
                                </h2>
                                <p className="mt-6 text-white/80 leading-relaxed text-lg">
                                    Camila tenía 12 años cuando llegó a Talentos en Acción. Tres años después
                                    pinta murales en su comunidad y enseña arte a niños más pequeños. Su
                                    transformación no es excepcional: es lo que pasa cuando las oportunidades
                                    encuentran las capacidades.
                                </p>
                                <Link to="/blog" className="btn-primary-pill mt-8 bg-white !text-brand-navy hover:bg-brand-cream" data-testid="impact-stories-cta">
                                    Leer más historias <ArrowRight size={16} />
                                </Link>
                            </div>
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden ring-4 ring-white/10">
                                <img src={IMAGES.children_community} alt="Historia comunitaria" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
